import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Space,
  Typography,
  Tabs,
  Form,
  Input,
  Select,
  Switch,
  Alert,
  Upload,
  Table,
  Modal,
  Tag,
  Popconfirm,
  Divider,
  Image,
  message
} from 'antd';
import {
  SettingOutlined,
  UserOutlined,
  SecurityScanOutlined,
  DatabaseOutlined,
  BellOutlined,
  TeamOutlined,
  ApiOutlined,
  SaveOutlined,
  ReloadOutlined,
  MonitorOutlined,
  AuditOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PhoneOutlined,
  MailOutlined,
  UploadOutlined,
  EyeOutlined,
  SendOutlined,
  RightOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiService from '../../../services/apiService';

const { Title } = Typography;
const { Option } = Select;

// TypeScript interfaces
interface CompanyAddress {
  id: string;
  type: 'delivery' | 'correspondence' | 'billing' | 'other';
  contactName: string;
  detailedAddress: string;
  country: string;
  phone?: string;
  isDefault: boolean;
}

interface CompanySettings {
  companyName: string;
  companyLogo: string;
  companyContactNumber: string;
  companyEmail: string;
  companyAddresses: CompanyAddress[];
}

const SettingsOverview: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    if (path.includes('/settings/general')) return 'general';
    if (path.includes('/settings/company')) return 'company';
    if (path.includes('/settings/users')) return 'users';
    if (path.includes('/settings/security')) return 'security';
    if (path.includes('/settings/integrations')) return 'integrations';
    if (path.includes('/settings/notifications')) return 'notifications';
    if (path.includes('/settings/backup')) return 'backup';
    if (path.includes('/settings/audit')) return 'audit';
    if (path.includes('/settings/preferences')) return 'preferences';
    if (path.includes('/settings/advanced')) return 'advanced';
    return 'general';
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  const [loading, setLoading] = useState(false);
  
  // Company settings state
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    companyName: 'WYNI AI ERP',
    companyLogo: '',
    companyContactNumber: '',
    companyEmail: '',
    companyAddresses: []
  });

  // Forms
  const [companyForm] = Form.useForm();
  const [addressForm] = Form.useForm();
  
  // Address management state
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false);
  const [editingAddress, setEditingAddress] = useState<CompanyAddress | null>(null);
  
  // Logo preview state
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [sfConfigs, setSfConfigs] = useState<any[]>([]);
  const [fedexConfigs, setFedexConfigs] = useState<any[]>([]);

  // 初始化/刷新載入公司設定
  const loadCompany = useCallback(async () => {
    try {
      setLoading(true);
      const resp: any = await apiService.getCompanySettings();
      const data = resp?.data || resp; // 後端統一 {success,data}；保守處理
      if (data?.settings) {
        const s = data.settings;
        const addr = data.addresses || [];
        const newState = {
          companyName: s.companyName || '',
          companyLogo: s.companyLogo || '',
          companyContactNumber: s.companyContactNumber || '',
          companyEmail: s.companyEmail || '',
          companyAddresses: addr,
        } as CompanySettings;
        setCompanySettings(newState);
        companyForm.setFieldsValue({
          companyName: newState.companyName,
          companyContactNumber: newState.companyContactNumber,
          companyEmail: newState.companyEmail,
          companyLogo: newState.companyLogo,
        });
        setLogoPreview(newState.companyLogo || '');
      }
    } catch (e) {
      // ignore -> 預設 mock
    } finally {
      setLoading(false);
    }
  }, [companyForm]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  // Load carrier configs (SF Express + FedEx) for dynamic Integrations cards
  useEffect(() => {
    (async () => {
      try {
        const [sfRes, fedexRes] = await Promise.all([
          apiService.listSFExpressConfigs() as Promise<any>,
          apiService.listFedExConfigs() as Promise<any>,
        ]);
        if (sfRes?.success) setSfConfigs(sfRes.data || []);
        if (fedexRes?.success) setFedexConfigs(fedexRes.data || []);
      } catch (e) {
        console.error('Failed to load carrier configs:', e);
      }
    })();
  }, []);

  // Company settings handlers
  const handleLogoUpload = (file: any) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setLogoPreview(result);
      setCompanySettings(prev => ({ ...prev, companyLogo: result }));
    };
    reader.readAsDataURL(file);
    return false; // Prevent default upload
  };

  const handleSaveCompanySettings = async (values: any) => {
    try {
      setLoading(true);
      const payload = {
        companyName: values.companyName,
        companyContactNumber: values.companyContactNumber,
        companyEmail: values.companyEmail,
        companyLogo: companySettings.companyLogo || logoPreview || '',
      };
      const resp: any = await apiService.updateCompanySettings(payload);
      const updated = resp?.data || resp;
      if (updated) {
        setCompanySettings(prev => ({ ...prev, ...payload }));
      }
      // 重新讀取地址（維持一致）
      const adrResp: any = await apiService.listCompanyAddresses();
      const addrs = adrResp?.data || adrResp || [];
      setCompanySettings(prev => ({ ...prev, companyAddresses: Array.isArray(addrs) ? addrs : [] }));
      message.success('Company settings saved successfully');
    } catch (error) {
      message.error('Failed to save company settings');
    } finally {
      setLoading(false);
    }
  };

  // Address management functions
  const handleAddAddress = () => {
    setEditingAddress(null);
    addressForm.resetFields();
    setIsAddressModalVisible(true);
  };

  const handleEditAddress = (address: CompanyAddress) => {
    setEditingAddress(address);
    addressForm.setFieldsValue(address);
    setIsAddressModalVisible(true);
  };

  const handleDeleteAddress = async (addressId: string) => {
    try {
      setLoading(true);
      const resp: any = await apiService.deleteCompanyAddress(addressId);
      const addrs = resp?.data || resp || [];
      setCompanySettings(prev => ({ ...prev, companyAddresses: Array.isArray(addrs) ? addrs : [] }));
      message.success('Address deleted successfully');
    } catch {
      message.error('Failed to delete address');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAddress = async (values: any) => {
    try {
      setLoading(true);
      const payload = {
        id: editingAddress?.id,
        ...values,
        isDefault: editingAddress ? values.isDefault ?? editingAddress.isDefault : companySettings.companyAddresses.length === 0,
      };
      const resp: any = await apiService.upsertCompanyAddress(payload);
      const addrs = resp?.data || resp || [];
      setCompanySettings(prev => ({ ...prev, companyAddresses: Array.isArray(addrs) ? addrs : [] }));
      setIsAddressModalVisible(false);
      message.success(`Address ${editingAddress ? 'updated' : 'added'} successfully`);
    } catch (error) {
      message.error('Failed to save address');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultAddress = async (addressId: string) => {
    try {
      const found = companySettings.companyAddresses.find(a => a.id === addressId);
      if (!found) return;
      setLoading(true);
      const resp: any = await apiService.upsertCompanyAddress({
        id: found.id,
        type: found.type,
        contactName: found.contactName,
        detailedAddress: found.detailedAddress,
        country: found.country,
        phone: found.phone,
        isDefault: true,
      });
      const addrs = resp?.data || resp || [];
      setCompanySettings(prev => ({ ...prev, companyAddresses: Array.isArray(addrs) ? addrs : [] }));
      message.success('Default address updated');
    } catch {
      message.error('Failed to set default address');
    } finally {
      setLoading(false);
    }
  };

  const renderGeneralSettings = () => (
    <Card title="General Settings">
      <Form layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Company Name">
                  <Input placeholder="Enter company name" />
                </Form.Item>
              </Col>
          <Col span={12}>
            <Form.Item label="Default Language">
              <Select defaultValue="en-US">
                <Option value="en-US">English (US)</Option>
                <Option value="zh-CN">中文 (简体)</Option>
                <Option value="zh-TW">中文 (繁體)</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Default Currency">
              <Select defaultValue="USD">
                    <Option value="USD">USD - US Dollar</Option>
                <Option value="CNY">CNY - Chinese Yuan</Option>
                    <Option value="EUR">EUR - Euro</Option>
                  </Select>
                </Form.Item>
              </Col>
          <Col span={12}>
            <Form.Item label="Timezone">
              <Select defaultValue="Asia/Hong_Kong">
                <Option value="Asia/Hong_Kong">Hong Kong Time (HKT)</Option>
                <Option value="UTC">UTC</Option>
                <Option value="America/New_York">Eastern Time</Option>
                <Option value="Asia/Shanghai">China Standard Time</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
        <Row gutter={16}>
        <Col span={24}>
            <Form.Item label="Enable Debug Mode" valuePropName="checked">
              <Switch />
                    </Form.Item>
        </Col>
      </Row>
    </Form>
    </Card>
  );

  const renderCompanySettings = () => (
    <div>
      {/* Company Information Card */}
      <Card title="Company Information" style={{ marginBottom: 24 }}>
        <Form
          form={companyForm}
          layout="vertical"
          initialValues={companySettings}
          onFinish={handleSaveCompanySettings}
        >
                      <Row gutter={[24, 16]}>
            {/* Left side - Company Information */}
            <Col xs={24} lg={16}>
            <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <Form.Item
                    name="companyName"
                    label="Company Name"
                    rules={[{ required: true, message: 'Please enter company name' }]}
                  >
                    <Input placeholder="Enter company name" size="large" />
                </Form.Item>
              </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="companyContactNumber"
                    label="Company Contact Number"
                    rules={[{ required: true, message: 'Please enter company contact number' }]}
                  >
                    <Input 
                      placeholder="Enter company contact number" 
                      prefix={<PhoneOutlined />}
                      size="large"
                    />
                </Form.Item>
              </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="companyEmail"
                    label="Company Email"
                    rules={[
                      { required: true, message: 'Please enter company email' },
                      { type: 'email', message: 'Please enter a valid email address' }
                    ]}
                  >
                    <Input 
                      placeholder="Enter company email" 
                      prefix={<MailOutlined />}
                      size="large"
                    />
                </Form.Item>
              </Col>
            </Row>
      </Col>

            {/* Right side - Company Logo */}
            <Col xs={24} lg={8}>
              <Form.Item name="companyLogo" label="Company Logo">
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Upload
                    name="logo"
                    listType="picture-card"
                    showUploadList={false}
                    beforeUpload={handleLogoUpload}
                    accept="image/*"
                    style={{ width: '200px', height: '200px' }}
                  >
                    {logoPreview || companySettings.companyLogo ? (
                      <div style={{ 
                        width: '200px', 
                        height: '200px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        border: '2px dashed #d9d9d9',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <Image
                          src={logoPreview || companySettings.companyLogo}
                          alt="Company Logo"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '100%', 
                            objectFit: 'contain' 
                          }}
                          preview={{
                            mask: <EyeOutlined />
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{ 
                        width: '200px', 
                        height: '200px', 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center',
                        border: '2px dashed #d9d9d9',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'border-color 0.3s'
                      }}>
                        <UploadOutlined style={{ fontSize: '32px', color: '#999', marginBottom: '8px' }} />
                        <div style={{ color: '#999', textAlign: 'center' }}>
                          Click to Upload Logo
                        </div>
                      </div>
                    )}
                  </Upload>
                </div>
                <div style={{ textAlign: 'center', marginTop: '8px', color: '#666', fontSize: '12px' }}>
                  Recommended size: 200x200px, supports JPG, PNG formats
                </div>
                </Form.Item>
              </Col>
            </Row>
          
          <Divider />
          
          <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />} size="large">
            Save Company Information
          </Button>
          </Form>
        </Card>

            {/* Company Addresses Card */}
            <Card 
        title="Company Addresses" 
        extra={
                  <Button 
                    type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddAddress}
                  >
            Add Address
                  </Button>
        }
      >
            <Table
          dataSource={companySettings.companyAddresses}
              columns={[
            {
              title: 'Type',
              dataIndex: 'type',
              key: 'type',
              render: (type: string) => {
                const typeMap = {
                  delivery: { label: 'Delivery', color: 'blue' },
                  correspondence: { label: 'Correspondence', color: 'green' },
                  billing: { label: 'Billing', color: 'orange' },
                  other: { label: 'Other', color: 'default' }
                };
                const typeInfo = typeMap[type as keyof typeof typeMap] || { label: type, color: 'default' };
                return (
                  <Tag color={typeInfo.color}>
                    {typeInfo.label}
                  </Tag>
                );
              }
            },
            {
              title: 'Contact Name',
              dataIndex: 'contactName',
              key: 'contactName'
            },
            {
              title: 'Address',
              key: 'address',
              render: (record: CompanyAddress) => (
                <div>
                  <div style={{ fontWeight: 500, marginBottom: '4px' }}>{record.detailedAddress}</div>
                  <div style={{ color: '#666', fontSize: '12px' }}>{record.country}</div>
                </div>
              )
            },
            {
              title: 'Phone',
              dataIndex: 'phone',
              key: 'phone'
            },
            {
              title: 'Default',
              key: 'default',
              render: (record: CompanyAddress) => (
                record.isDefault ? (
                  <Tag color="success">Default</Tag>
                ) : (
                  <Button 
                    size="small" 
                    onClick={() => handleSetDefaultAddress(record.id)}
                  >
                    Set Default
                  </Button>
                )
              )
            },
                {
                  title: 'Actions',
                  key: 'actions',
              render: (record: CompanyAddress) => (
                <Space>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => handleEditAddress(record)}
                  />
                  <Popconfirm
                    title={record.isDefault && companySettings.companyAddresses.length <= 1
                      ? 'Delete the only/default address? This cannot be undone.'
                      : 'Are you sure you want to delete this address?'}
                    onConfirm={() => handleDeleteAddress(record.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={loading}
                      loading={loading}
                    />
                  </Popconfirm>
                </Space>
              )
                }
              ]}
          rowKey="id"
              pagination={false}
          locale={{ emptyText: 'No addresses configured. Click "Add Address" to get started.' }}
            />
        </Card>
    </div>
  );

    const renderAddressModal = () => (
    <Modal
      title={editingAddress ? 'Edit Address' : 'Add New Address'}
      open={isAddressModalVisible}
      onCancel={() => setIsAddressModalVisible(false)}
      footer={null}
      width={600}
    >
      <Form
        form={addressForm}
        layout="vertical"
        onFinish={handleSaveAddress}
      >
            <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
                        <Form.Item
              name="type"
              label="Address Type"
              rules={[{ required: true, message: 'Please select address type' }]}
            >
              <Select placeholder="Select address type">
                <Option value="delivery">Delivery</Option>
                <Option value="correspondence">Correspondence</Option>
                <Option value="billing">Billing</Option>
                <Option value="other">Other</Option>
              </Select>
                </Form.Item>
              </Col>
          <Col xs={24} sm={12}>
                        <Form.Item
              name="contactName"
              label="Contact Name"
              rules={[{ required: true, message: 'Please enter contact name' }]}
            >
              <Input placeholder="Enter contact name" />
                </Form.Item>
              </Col>
          <Col xs={24} sm={12}>
                        <Form.Item
              name="country"
              label="Country/Region"
              rules={[{ required: true, message: 'Please select country/region' }]}
            >
              <Select placeholder="Select country/region" showSearch>
                <Option value="Hong Kong">Hong Kong</Option>
                <Option value="China">China</Option>
                <Option value="Taiwan">Taiwan</Option>
                <Option value="Macau">Macau</Option>
                <Option value="Singapore">Singapore</Option>
                <Option value="Malaysia">Malaysia</Option>
                <Option value="USA">USA</Option>
                <Option value="Canada">Canada</Option>
                <Option value="UK">UK</Option>
                <Option value="Australia">Australia</Option>
              </Select>
                </Form.Item>
              </Col>
          <Col xs={24} sm={12}>
                        <Form.Item
              name="phone"
              label="Phone Number"
            >
              <Input placeholder="Enter phone number (optional)" prefix={<PhoneOutlined />} />
                </Form.Item>
              </Col>
          <Col xs={24}>
                        <Form.Item
              name="detailedAddress"
              label="Detailed Address"
              rules={[{ required: true, message: 'Please enter detailed address' }]}
            >
              <Input.TextArea 
                rows={3} 
                placeholder="Enter detailed address including street, building name, floor and unit number"
                showCount
                maxLength={200}
              />
            </Form.Item>
              </Col>
            </Row>
        
        <Divider />
        
          <Space>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
            {editingAddress ? 'Update Address' : 'Add Address'}
          </Button>
          <Button onClick={() => setIsAddressModalVisible(false)}>
            Cancel
          </Button>
          </Space>
      </Form>
    </Modal>
  );

  const tabItems = [
    {
      key: 'general',
      label: (
        <span>
          <SettingOutlined />
          General
        </span>
      ),
      children: renderGeneralSettings()
    },
    {
      key: 'company',
      label: (
        <span>
          <DatabaseOutlined />
          Company
        </span>
      ),
      children: renderCompanySettings()
    },
    {
      key: 'users',
      label: (
        <span>
          <TeamOutlined />
          Users
        </span>
      ),
      children: (
          <Alert
            message="User Management"
            description="User management functionality is available in the dedicated Users section."
            type="info"
            showIcon
        />
      )
    },
    {
      key: 'security',
      label: (
        <span>
          <SecurityScanOutlined />
          Security
        </span>
      ),
      children: (
        <Alert
          message="Security Settings"
          description="Configure security settings including password policies and access controls."
          type="info"
          showIcon
        />
      )
    },
    {
      key: 'integrations',
      label: (
        <span>
          <ApiOutlined />
          Integrations
        </span>
      ),
      children: (
        <div>
          <Alert
            message="Integration Settings"
            description="Configure third-party integrations and API settings."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
          <Row gutter={[16, 16]}>
            {/* Dynamic FedEx cards */}
            {fedexConfigs.map((cfg: any) => (
              <Col xs={24} md={12} key={`fedex-${cfg.id}`}>
                <Card
                  title={
                    <span>
                      <SendOutlined style={{ marginRight: 8 }} />
                      FedEx 配置
                      {cfg.linkedCustomers?.length === 1 ? ` - ${cfg.linkedCustomers[0].name}` : ''}
                    </span>
                  }
                  hoverable
                  onClick={() => navigate(`/settings/fedex?configId=${cfg.id}`)}
                  style={{ cursor: 'pointer' }}
                  extra={<RightOutlined />}
                >
                  <p>配置 FedEx API 憑證以啟用物流追蹤和運單建立功能。</p>
                  <Button type="primary" onClick={(e) => { e.stopPropagation(); navigate(`/settings/fedex?configId=${cfg.id}`); }}>
                    前往設定
                  </Button>
                </Card>
              </Col>
            ))}

            {/* Dynamic SF Express cards (standard + b2b mixed, ordered by created_at) */}
            {sfConfigs.map((cfg: any) => {
              const isB2B = cfg.accountType === 'b2b';
              const titleBase = isB2B ? '順豐速運 B2B 配置' : '順豐速運配置 (標準)';
              const title = cfg.displayName ? `${titleBase} - ${cfg.displayName}` : titleBase;
              const editPath = `/settings/sf-express?configId=${cfg.id}`;
              return (
                <Col xs={24} md={12} key={`sf-${cfg.id}`}>
                  <Card
                    title={
                      <span>
                        <SendOutlined style={{ marginRight: 8 }} />
                        {title}
                      </span>
                    }
                    hoverable
                    onClick={() => navigate(editPath)}
                    style={{ cursor: 'pointer' }}
                    extra={<RightOutlined />}
                  >
                    <p>{isB2B
                      ? '配置 SF Express B2B 帳戶 API 憑證,用於企業對企業寄件服務。'
                      : '配置 SF Express API 憑證以啟用順豐速運下單和物流追蹤功能。'}</p>
                    <Button type="primary" onClick={(e) => { e.stopPropagation(); navigate(editPath); }}>
                      前往設定
                    </Button>
                  </Card>
                </Col>
              );
            })}

            {/* "+ Add" cards */}
            <Col xs={24} md={12}>
              <Card hoverable onClick={() => navigate('/settings/sf-express?new=1&accountType=standard')} style={{ cursor: 'pointer', borderStyle: 'dashed' }}>
                <Button type="dashed" block icon={<PlusOutlined />}>新增順豐速運帳號 (標準)</Button>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card hoverable onClick={() => navigate('/settings/sf-express?new=1&accountType=b2b')} style={{ cursor: 'pointer', borderStyle: 'dashed' }}>
                <Button type="dashed" block icon={<PlusOutlined />}>新增順豐速運帳號 (B2B)</Button>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card hoverable onClick={() => navigate('/settings/fedex?new=1')} style={{ cursor: 'pointer', borderStyle: 'dashed' }}>
                <Button type="dashed" block icon={<PlusOutlined />}>新增 FedEx 帳號</Button>
              </Card>
            </Col>
          </Row>
        </div>
      )
    },
    {
      key: 'notifications',
      label: (
        <span>
          <BellOutlined />
          Notifications
        </span>
      ),
      children: (
          <Alert
            message="Notification Settings"
          description="Configure email and system notifications."
            type="info"
            showIcon
        />
      )
    },
    {
      key: 'backup',
      label: (
        <span>
          <DatabaseOutlined />
          Backup
        </span>
      ),
      children: (
          <Alert
          message="Backup Settings"
          description="Configure automated backup settings and schedules."
            type="info"
            showIcon
        />
      )
    },
    {
      key: 'audit',
      label: (
        <span>
          <AuditOutlined />
          Audit
        </span>
      ),
      children: (
        <Alert
          message="Audit Log Settings"
          description="Configure audit logging and monitoring settings."
          type="info"
          showIcon
        />
      )
    },
    {
      key: 'preferences',
      label: (
        <span>
          <UserOutlined />
          Preferences
        </span>
      ),
      children: (
        <Alert
          message="User Preferences"
          description="Configure personal user preferences and display settings."
          type="info"
          showIcon
        />
      )
    },
    {
      key: 'advanced',
      label: (
        <span>
          <MonitorOutlined />
          Advanced
        </span>
      ),
      children: (
        <Alert
          message="Advanced Settings"
          description="Advanced system configuration options."
          type="warning"
          showIcon
        />
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>System Settings</Title>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        tabBarExtraContent={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadCompany} disabled={loading}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={loading}
              onClick={async () => {
                try {
                  const values = await companyForm.validateFields();
                  await handleSaveCompanySettings(values);
                } catch {
                  // validate failed -> no-op
                }
              }}
            >
              Save All
            </Button>
          </Space>
        }
      />
      
      {/* Address Modal */}
      {renderAddressModal()}
    </div>
  );
};

export default SettingsOverview; 